import React from 'react';
import { Bar } from 'react-chartjs-2';
import { LoadingSpinner, ErrorMessage, EmptyState } from '../ui';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartBackgroundColor, getChartJsOptions, getChartColors } from '../../utils/themeHelpers';
import { getResponsiveXAxisTicks, isMobileChartViewport } from '../../utils/chartResponsive';
import { zeroBaselinePlugin } from '../../utils/chartPlugins';
import { CHART_GUIDES } from '../../constants/chartGuides';
import ChartControls from '../ui/ChartControls';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import useChartGuide from '../../hooks/useChartGuide';
import {
  activeSeries,
  formatPercentTick,
  formatSignedPercent,
  getMobileGroupLabels,
  getControllerButtonClass,
  getGrowthPercent,
  getSortedPurchaseData,
  getSymmetricAxisBounds,
  getZeroLineGrid,
  numeric,
  toGrowthLabel,
  useCustomerPurchaseBehaviorData,
  valueLabelPlugin,
  CustomerPurchaseBehaviorChartProps,
} from './customerPurchaseBehaviorData';

const CustomerPurchaseBehaviorGroupGrowthByTransition: React.FC<CustomerPurchaseBehaviorChartProps> = ({
  branchId,
  purchaseData,
  onRetry,
}) => {
  const { currentTheme } = useTheme();
  const chartColors = getChartColors(currentTheme);
  const groupGrowthChartRef = React.useRef<HTMLDivElement>(null);
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { openGuide } = useChartGuide();
  const [selectedTransitionLabel, setSelectedTransitionLabel] = React.useState<string>('');
  const { data: fetchedData, isLoading, error, refetch } = useCustomerPurchaseBehaviorData(branchId, {
    enabled: purchaseData === undefined,
  });
  const data = purchaseData ?? fetchedData;
  const showLoading = purchaseData === undefined && isLoading;
  const queryError = purchaseData === undefined ? error : null;
  const retry = onRetry ?? refetch;
  const sortedData = getSortedPurchaseData(data);
  // میلهٔ نخست به‌جای «بدون اشتراک»، تغییر «نرخ اشتراک‌گیری» را نشان می‌دهد.
  const groupSeries = [
    { key: 'SubscriptionCount', label: 'اشتراک‌گیری' },
    ...activeSeries.slice(1),
  ];
  // تعداد مشترکان هر ماه = مجموع خریدها (Purchase_1..6Plus)
  const getSubscriptionCount = (item: any): number => (
    numeric(item.Purchase_1) + numeric(item.Purchase_2) + numeric(item.Purchase_3)
    + numeric(item.Purchase_4) + numeric(item.Purchase_5) + numeric(item.Purchase_6Plus)
  );
  // نرخ اشتراک‌گیری هر ماه (درصد) = مشترکان ÷ کل مشتریان × ۱۰۰
  const getSubscriptionRate = (item: any): number => {
    const total = activeSeries.reduce((sum, series) => sum + numeric(item[series.key]), 0);
    return total === 0 ? 0 : (getSubscriptionCount(item) / total) * 100;
  };
  const getSeriesValue = (item: any, key: string): number => (
    key === 'SubscriptionCount' ? getSubscriptionCount(item) : numeric(item[key])
  );
  const groupLabelsWithTotal = getMobileGroupLabels([
    ...groupSeries.map((series) => series.label),
    'کل سفارش‌ها',
  ]);
  const isMobileChart = isMobileChartViewport();

  const monthlyGrowthByGroupCharts = sortedData.slice(1).map((currentItem, idx) => {
    const previousItem = sortedData[idx];
    // برای اشتراک‌گیری: تفاضل نرخ دو ماه (واحد: درصد). برای بقیه: درصد رشد نسبت به ماه قبل.
    const groupPercentValues = groupSeries.map((series) => (
      series.key === 'SubscriptionCount'
        ? getSubscriptionRate(currentItem) - getSubscriptionRate(previousItem)
        : getGrowthPercent(getSeriesValue(currentItem, series.key), getSeriesValue(previousItem, series.key))
    ));
    const groupCountValues = groupSeries.map((series) => (
      getSeriesValue(currentItem, series.key) - getSeriesValue(previousItem, series.key)
    ));

    const currentTotal = activeSeries.reduce((sum, series) => sum + numeric(currentItem[series.key]), 0);
    const previousTotal = activeSeries.reduce((sum, series) => sum + numeric(previousItem[series.key]), 0);
    const totalGrowth = getGrowthPercent(currentTotal, previousTotal);
    const totalCountChange = currentTotal - previousTotal;
    const returningCurrent = numeric(currentItem.Purchase_2) + numeric(currentItem.Purchase_3) + numeric(currentItem.Purchase_4)
      + numeric(currentItem.Purchase_5) + numeric(currentItem.Purchase_6Plus);
    const returningPrevious = numeric(previousItem.Purchase_2) + numeric(previousItem.Purchase_3) + numeric(previousItem.Purchase_4)
      + numeric(previousItem.Purchase_5) + numeric(previousItem.Purchase_6Plus);
    const onePurchaseGrowth = getGrowthPercent(
      numeric(currentItem.Purchase_1),
      numeric(previousItem.Purchase_1)
    );
    const returningGrowth = getGrowthPercent(returningCurrent, returningPrevious);
    // رشد اشتراک‌گیری = تفاضل نرخ اشتراک‌گیری ماه فعلی و ماه قبل (واحد: درصد)
    const subscriptionGrowth = getSubscriptionRate(currentItem) - getSubscriptionRate(previousItem);

    return {
      transitionLabel: toGrowthLabel(previousItem.YearMonth, currentItem.YearMonth),
      percentValues: [...groupPercentValues, totalGrowth],
      countValues: [...groupCountValues, totalCountChange],
      indicators: [
        { label: 'مجموع سفارش‌ها', value: totalGrowth },
        { label: 'مشتریان تک‌خرید', value: onePurchaseGrowth },
        { label: 'مشتریان بازگشتی (۲ خرید به بالا)', value: returningGrowth },
        { label: 'اشتراک‌گیری', value: subscriptionGrowth },
      ],
    };
  });
  const monthlyGrowthByGroupChartsLatestFirst = [...monthlyGrowthByGroupCharts].reverse();
  const defaultTransitionLabel = monthlyGrowthByGroupChartsLatestFirst[0]?.transitionLabel || '';

  React.useEffect(() => {
    if (!monthlyGrowthByGroupChartsLatestFirst.length) {
      return;
    }
    if (!selectedTransitionLabel) {
      setSelectedTransitionLabel(defaultTransitionLabel);
      return;
    }
    const stillExists = monthlyGrowthByGroupChartsLatestFirst.some(
      (item: any) => item.transitionLabel === selectedTransitionLabel
    );
    if (!stillExists) {
      setSelectedTransitionLabel(monthlyGrowthByGroupChartsLatestFirst[0].transitionLabel);
    }
  }, [monthlyGrowthByGroupChartsLatestFirst, defaultTransitionLabel, selectedTransitionLabel]);

  if (!branchId) {
    return (
      <div dir="rtl" className="card-base card-padding border border-status-warning" role="alert">
        برای دیدن این داشبورد، ابتدا شعبه را انتخاب کنید.
      </div>
    );
  }

  if (showLoading) {
    return <LoadingSpinner variant="dashboard" message="در حال بارگذاری رفتار خرید مشتریان…" />;
  }

  if (queryError) {
    return (
      <div className="card-base card-padding">
        <ErrorMessage
          message="دریافت اطلاعات رفتار خرید مشتریان با خطا مواجه شد. لطفاً دوباره تلاش کنید."
          onRetry={() => {
            retry();
          }}
        />
      </div>
    );
  }

  if (!sortedData.length) {
    return (
      <div className="card-base card-padding">
        <EmptyState message="برای این شعبه هنوز اطلاعات رفتار خریدی ثبت نشده است" />
      </div>
    );
  }

  const selectedMonthlyGrowthChart = monthlyGrowthByGroupChartsLatestFirst.find(
    (item: any) => item.transitionLabel === selectedTransitionLabel
  ) || monthlyGrowthByGroupChartsLatestFirst[0];
  const selectedGroupGrowthValues = selectedMonthlyGrowthChart
    .percentValues.filter((value: any) => value !== null && value !== undefined);
  const selectedGroupAxisBounds = getSymmetricAxisBounds(selectedGroupGrowthValues, 6);

  return (
    <div ref={groupGrowthChartRef} className="card-base card-padding animate-enter animate-delay-3">
      <ChartControls
        componentId="customer-purchase-behavior-group-growth"
        componentTitle="رشد هر گروه نسبت به ماه قبل"
        componentType="chart"
        dashboardSource="customerPurchaseBehavior"
        onInfo={() => openGuide(CHART_GUIDES.purchaseBehaviorGroupGrowthPercent)}
        onFavorite={() => handleFavoriteToggle({
          componentId: 'customer-purchase-behavior-group-growth',
          componentTitle: 'رشد هر گروه نسبت به ماه قبل',
          dashboardSource: 'customerPurchaseBehavior',
          componentType: 'chart',
          description: 'نرخ رشد هر گروه مشتری بین دو ماه انتخابی',
          props: { branchId },
        })}
        isFavorited={isFavorited('customer-purchase-behavior-group-growth')}
        chartRef={groupGrowthChartRef}
        className="mb-4"
      />
      <h3 className="title-card">رشد هر گروه نسبت به ماه قبل</h3>
      <div role="group" aria-label="انتخاب ماه مقایسه رشد" className="cpb-month-picker mb-4 lg:flex-wrap" style={{ paddingTop: 12 }}>
        {monthlyGrowthByGroupChartsLatestFirst.map((item: any) => {
          const isActive = item.transitionLabel === selectedMonthlyGrowthChart?.transitionLabel;
          return (
            <button
              key={item.transitionLabel}
              type="button"
              onClick={() => setSelectedTransitionLabel(item.transitionLabel)}
              className={getControllerButtonClass(isActive)}
              title={`نمایش نمودار ${item.transitionLabel}`}
              aria-pressed={isActive}
            >
              {item.transitionLabel}
            </button>
          );
        })}
      </div>
      {selectedMonthlyGrowthChart && (
        <div className="border rounded-lg p-3 md:p-4">
          <h4 className="text-sm md:text-base font-semibold mb-3 text-text-primary text-center">
            {`رشد ${selectedMonthlyGrowthChart.transitionLabel}`}
          </h4>
          <div className="mb-3 flex flex-wrap items-center justify-center gap-2 text-xs md:text-sm">
            {selectedMonthlyGrowthChart.indicators.map((indicator: any) => {
              const isNeutral = indicator.value === null || Math.abs(Number(indicator.value)) < 0.05;
              const isPositive = Number(indicator.value) > 0;
              // اشتراک‌گیری همیشه با آیکون دایره نمایش داده می‌شود (به‌جای فلش بالا/پایین)
              const isSubscription = indicator.label === 'اشتراک‌گیری';
              const arrow = isSubscription ? '●' : (isNeutral ? '•' : (isPositive ? '▲' : '▼'));
              const color = isNeutral ? chartColors[3 % chartColors.length] : (isPositive ? '#16a34a' : '#dc2626');
              const valueText = indicator.value === null ? '—' : formatSignedPercent(Number(indicator.value), 1);
              return (
                <span
                  key={indicator.label}
                  style={{ color }}
                  className="px-2 py-0.5 rounded-full border border-current/20"
                >
                  {`${arrow} ${indicator.label}: ${valueText}`}
                </span>
              );
            })}
          </div>
          <div
            role="img"
            aria-label={`نمودار رشد ${selectedMonthlyGrowthChart.transitionLabel} به تفکیک گروه مشتری`}
            className="mobile-chart-host mobile-chart-host--category-bars w-full"
          >
            <Bar
              plugins={[...(isMobileChart ? [zeroBaselinePlugin as any] : [valueLabelPlugin as any, zeroBaselinePlugin as any])]}
              data={{
                labels: groupLabelsWithTotal,
                datasets: [
                  {
                    type: 'bar',
                    label: 'درصد رشد',
                    data: selectedMonthlyGrowthChart.percentValues,
                    backgroundColor: groupLabelsWithTotal.map((_, groupIndex) => getChartBackgroundColor(groupIndex, currentTheme, 0.75)),
                    borderColor: groupLabelsWithTotal.map((_, groupIndex) => chartColors[groupIndex % chartColors.length]),
                    borderWidth: 1,
                    borderRadius: 6,
                    isPercent: true,
                  } as any,
                ] as any,
              }}
              options={{
                ...getChartJsOptions(currentTheme),
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                  mode: 'index',
                  intersect: false,
                },
                scales: {
                  x: {
                    ...getChartJsOptions(currentTheme).scales?.x,
                    ticks: getResponsiveXAxisTicks(
                      getChartJsOptions(currentTheme).scales?.x?.ticks as Record<string, unknown> | undefined,
                    ),
                  },
                  y: {
                    ...getChartJsOptions(currentTheme).scales?.y,
                    grid: getZeroLineGrid(getChartJsOptions(currentTheme).scales?.y?.grid),
                    min: selectedGroupAxisBounds.min,
                    max: selectedGroupAxisBounds.max,
                    ticks: {
                      ...getChartJsOptions(currentTheme).scales?.y?.ticks,
                      callback: (value: any) => formatPercentTick(Number(value || 0)),
                      stepSize: selectedGroupAxisBounds.stepSize,
                    },
                    title: {
                      display: !isMobileChart,
                      text: 'درصد رشد از هر ماه به ماه بعد',
                    },
                  },
                },
                plugins: {
                  ...getChartJsOptions(currentTheme).plugins,
                  ...(!isMobileChart ? ({
                    valueLabelPlugin: {
                      minPixelHeight: 14,
                      shortBarThreshold: 22,
                      minAbsValue: 1,
                      percentDecimals: 1,
                      avoidOverlap: false,
                      labelPosition: 'center',
                      showBackground: false,
                      textColor: 'rgba(17, 24, 39, 0.92)',
                      fontSize: 11,
                    },
                  } as any) : {}),
                  legend: {
                    ...getChartJsOptions(currentTheme).plugins?.legend,
                    display: false,
                  },
                  tooltip: {
                    ...getChartJsOptions(currentTheme).plugins?.tooltip,
                    callbacks: {
                      title: () => selectedMonthlyGrowthChart.transitionLabel,
                      label: (context: any) => {
                        const separator = '\u00A0:\u00A0';
                        return `${context.label}${separator}${formatSignedPercent(Number(context.parsed.y), 1)}`;
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerPurchaseBehaviorGroupGrowthByTransition;
